/**
 * TirelessSkillEffect — Specter Veil/Broadcast terminal passive.
 *
 * Running drains stamina at half the normal rate. Sprint longer, recover
 * faster, keep the noise going — the operator who weaponizes sound gets
 * to stay loud without paying full price.
 *
 * On equip: sets `drainMultiplier` on StaminaComponent to 0.5.
 * On unequip: resets `drainMultiplier` to 1.0 (default).
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-080 (terminal passive pattern)
 * @see specter-drift-path-proposal.md § Tireless
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface TirelessDeps {
  readonly stamina: {
    /** Multiplier applied to stamina drain rate (default 1.0). */
    drainMultiplier: number;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface TirelessConfig {
  /** Drain rate multiplier to apply (0.5 = half drain). */
  readonly drainMultiplier: number;
}

const DEFAULT_CONFIG: TirelessConfig = {
  drainMultiplier: 0.5,
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class TirelessSkillEffect implements SkillEffect {
  readonly skillId = 'tireless';
  readonly type = 'passive' as const;
  readonly activationMode = 'instant' as const;
  readonly staminaCost: number = 0;

  private readonly deps: TirelessDeps;
  private readonly config: TirelessConfig;

  /** Whether the multiplier has been applied (guards against double-application). */
  private _applied = false;

  constructor(deps: TirelessDeps, config: Partial<TirelessConfig> = {}) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- SkillEffect interface ---

  canActivate(): boolean { return false; }
  activate(): boolean { return false; }
  deactivate(): void {}
  isActive(): boolean { return false; }

  update(_delta: number): void {
    if (!this._applied) {
      this.deps.stamina.drainMultiplier = this.config.drainMultiplier;
      this._applied = true;
    }
  }

  destroy(): void {
    if (this._applied) {
      this.deps.stamina.drainMultiplier = 1.0;
      this._applied = false;
    }
  }
}
